import { useEffect, useRef } from 'react';
import { useSimulationStore } from '../store/useSimulationStore';
import { useAuthStore, authHeaders } from '../store/useAuthStore';
import { apiUrl, wsUrl } from '../utils/api';

interface WSMessage {
  type: 'INITIAL_STATE' | 'NODE_UPDATE' | 'NODE_CREATED' | 'NODE_DELETED'
      | 'CONNECTION_CREATED' | 'CONNECTION_DELETED'
      | 'RESOURCE_NODE_CREATED' | 'RESOURCE_NODE_DELETED'
      | 'SIMULATION_STATUS' | 'ECHO';
  tick?: number;
  nodes?: any[];
  node?: any;
  connections?: any[];
  connection?: any;
  resource_nodes?: any[];
  resource_node?: any;
  alerts?: any[];
  investments?: any[];
  id?: number;
  paused?: boolean;
  data?: any;
}

export const useWebSocket = () => {
  const {
    setNodes, setConnections, updateNodes, updateConnections,
    addNode, removeNode, addConnection, removeConnection,
    setConnected, setReconnecting, setTick,
    setResourceNodes, addResourceNode, removeResourceNode,
    setSimulationPaused, setAlerts, setInvestments,
  } = useSimulationStore();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const token = useAuthStore(s => s.token);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = () => {
    if (!token) return;
    setReconnecting(false);

    const hdrs = authHeaders();

    // Initial Fetch
    fetch(apiUrl('/api/nodes'), { headers: hdrs })
      .then(res => res.json())
      .then(data => setNodes(data))
      .catch(err => console.error('Error fetching nodes:', err));
    
    // Connections fetching for fallback
    fetch(apiUrl('/api/connections'), { headers: hdrs })
       .then(res => res.json())
       .then(data => setConnections(data))
       .catch(err => console.error('Error fetching connections:', err));

    // Resources fetching for fallback
    fetch(apiUrl('/api/resources'), { headers: hdrs })
       .then(res => res.json())
       .then(data => setResourceNodes(data))
       .catch(err => console.error('Error fetching resources:', err));

    // Alerts fetching for fallback
    fetch(apiUrl('/api/alerts'), { headers: hdrs })
       .then(res => res.json())
       .then(data => { if (Array.isArray(data)) setAlerts(data); })
       .catch(err => console.error('Error fetching alerts:', err));
    // Investments fetching for fallback
    fetch(apiUrl('/api/resources'), { headers: hdrs })
       .then(res => res.json())
       .then(async (rns: any[]) => {
         const hdrs2 = authHeaders();
         const all: any[] = [];
         await Promise.all(rns.map(rn =>
           fetch(apiUrl(`/api/resources/${rn.id}/investments`), { headers: hdrs2 })
             .then(r => r.json())
             .then(invs => { if (Array.isArray(invs)) all.push(...invs); })
             .catch(() => {})
         ));
         setInvestments(all);
       })
       .catch(err => console.error('Error fetching investments:', err));

    const wsUrlStr = wsUrl('/api/ws', { token });
    
    console.log('Connecting to WebSocket:', wsUrlStr);
    const ws = new WebSocket(wsUrlStr);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket Connected');
      setConnected(true);
      setReconnecting(false);
    };
    
    ws.onmessage = (event) => {
      try {
        const message: WSMessage = JSON.parse(event.data);
        console.log(`[WS] Received message type=${message.type}, tick=${message.tick}, nodes=${message.nodes?.length ?? 'n/a'}, connections=${message.connections?.length ?? 'n/a'}, alerts=${message.alerts?.length ?? 'n/a'}`);
        if (message.type === 'INITIAL_STATE' && message.nodes) {
          console.log('[WS] INITIAL_STATE — setting all state');
          setNodes(message.nodes);
          if (message.connections) setConnections(message.connections);
          if (message.resource_nodes) setResourceNodes(message.resource_nodes);
          if (message.tick != null) setTick(message.tick);
          if (message.paused != null) setSimulationPaused(message.paused);
        } else if (message.type === 'NODE_UPDATE') {
          console.log('[WS] NODE_UPDATE — calling updateNodes with', message.nodes?.length, 'nodes');
          if (message.nodes && message.nodes.length > 0) {
            console.log('[WS] NODE_UPDATE sample node[0]:', JSON.stringify(message.nodes[0]));
          }
          if (message.nodes) updateNodes(message.nodes);
          if (message.connections) setConnections(message.connections);
          if (message.alerts) setAlerts(message.alerts);
          if (message.investments) setInvestments(message.investments);
          if (message.tick != null) setTick(message.tick);
          console.log('[WS] NODE_UPDATE — store calls complete');
        } else if (message.type === 'NODE_CREATED' && message.node) {
          addNode(message.node);
        } else if (message.type === 'NODE_DELETED' && message.id != null) {
          removeNode(message.id);
        } else if (message.type === 'CONNECTION_CREATED' && message.connection) {
          addConnection(message.connection);
        } else if (message.type === 'CONNECTION_DELETED' && message.id != null) {
          removeConnection(message.id);
        } else if (message.type === 'RESOURCE_NODE_CREATED' && message.resource_node) {
          addResourceNode(message.resource_node);
        } else if (message.type === 'RESOURCE_NODE_DELETED' && message.id != null) {
          removeResourceNode(message.id);
        } else if (message.type === 'SIMULATION_STATUS' && message.paused != null) {
          setSimulationPaused(message.paused);
        }
      } catch (err) {
        console.error('Error parsing WS message:', err);
      }
    };

    ws.onclose = (event) => {
      setConnected(false);
      if (event.code !== 1000) {
        console.log('WebSocket closed unexpectedly. Reconnecting...');
        setReconnecting(true);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket Error:', err);
    };
  };

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    let isMounted = true;
    
    // Delay connection slightly to handle React StrictMode double-invoke
    const connectTimeout = setTimeout(() => {
      if (isMounted) {
        connect();
      }
    }, 100);
    
    return () => {
      isMounted = false;
      clearTimeout(connectTimeout);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, token]);

  return { isConnected: useSimulationStore(state => state.isConnected) };
};
